import { memo, useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
  FormControlLabel,
  Alert,
  Divider,
} from '@mui/material';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import SmsIcon from '@mui/icons-material/Sms';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import LocalPostOfficeIcon from '@mui/icons-material/LocalPostOffice';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useLanguage } from '../../../../context/LanguageContext';
import { useLayoutPreferences } from '../../../../context/LayoutPreferencesContext';
import { useUser } from '../../../../context/UserContext';

export type ChannelType = 'email' | 'sms' | 'cashdesk' | 'wallet' | 'courrier' | '';

// Only Weldom runs test operations through this flag today — other workspaces don't use it, so the
// checkbox stays hidden for them rather than showing a control that would do nothing.
const TEST_OPERATION_WORKSPACE = 'WELDOM';

export interface DefinitionFormData {
  channel: ChannelType;
  title: string;
  description: string;
  sendDate: string;
  sendByCustomer: boolean;
  hasOffer: boolean;
  followUpDateStart: string;
  followUpDateEnd: string;
  isTestOperation: boolean;
  isRecurring: boolean;
  editionDateStart: string;
  editionDateEnd: string;
}

interface CommunicationDefinitionStepProps {
  data: DefinitionFormData;
  onChange: (patch: Partial<DefinitionFormData>) => void;
}

const CHANNELS = [
  { value: 'courrier', label: 'Courrier', Icon: LocalPostOfficeIcon      },
  { value: 'email',    label: 'Email',    Icon: AlternateEmailIcon       },
  { value: 'sms',      label: 'SMS',      Icon: SmsIcon                  },
  { value: 'wallet',   label: 'Wallet',   Icon: AccountBalanceWalletIcon },
  { value: 'cashdesk', label: 'Caisse',   Icon: PointOfSaleIcon          },
];

function CommunicationDefinitionStep({ data, onChange }: CommunicationDefinitionStepProps) {
  const { t } = useLanguage();
  const { sidebarColor } = useLayoutPreferences();
  const { selectedWorkspace } = useUser();
  const isTestOperationWorkspace = selectedWorkspace?.volumeName?.toUpperCase() === TEST_OPERATION_WORKSPACE;

  const [title, setTitle] = useState(data.title);
  const [description, setDescription] = useState(data.description);
  const [sendDate, setSendDate] = useState(data.sendDate);
  const [followUpDateStart, setFollowUpDateStart] = useState(data.followUpDateStart);
  const [followUpDateEnd, setFollowUpDateEnd] = useState(data.followUpDateEnd);
  const [editionDateStart, setEditionDateStart] = useState(data.editionDateStart);
  const [editionDateEnd, setEditionDateEnd] = useState(data.editionDateEnd);
  const isCashdeskChannel = data.channel === 'cashdesk';

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
        {t('pages.marketing.communication.definition')}
      </Typography>

      <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
        {t('pages.marketing.communication.canal')}
      </Typography>

      <Grid container spacing={2} alignItems="flex-start">
        <Grid size={{ xs: 12, md: 7 }}>
          <ToggleButtonGroup
            value={data.channel}
            exclusive
            onChange={(_, val) => {
              if (val) onChange({ channel: val as ChannelType });
            }}
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}
          >
            {CHANNELS.map(({ value, label, Icon }) => (
              <ToggleButton
                key={value}
                value={value}
                sx={{
                  flexDirection: 'column',
                  gap: 0.5,
                  width: 110,
                  py: 1.2,
                  border: '1px solid #dee2e6 !important',
                  borderRadius: '8px !important',
                  color: '#495057',
                  '&.Mui-selected': {
                    backgroundColor: `${sidebarColor}15`,
                    color: sidebarColor,
                    borderColor: `${sidebarColor} !important`,
                    fontWeight: 600,
                  },
                  '&:hover': {
                    backgroundColor: `${sidebarColor}10`,
                  },
                }}
              >
                <Icon fontSize="small" />
                <Typography variant="caption" sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
                  {label}
                </Typography>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Alert
            icon={<InfoOutlinedIcon fontSize="small" />}
            severity="info"
            sx={{ fontSize: '0.8rem', borderRadius: 2 }}
          >
            <Typography variant="caption" fontWeight={600} display="block">
              Rappel: Définition, programmation, template 2
            </Typography>
            <Typography variant="caption">
              Votre communication par <strong>email</strong> est entièrement paramétrable. Mais rassurez-vous des modèles sont à votre disposition.
            </Typography>
          </Alert>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Grid container spacing={2}>
        <Grid size={12}>
          <TextField
            fullWidth
            required
            label={t('pages.marketing.communication.title')}
            value={title}
            onChange={e => setTitle(e.target.value)}
            onBlur={e => onChange({ title: e.target.value })}
            size="small"
          />
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('pages.marketing.communication.description')}
            value={description}
            onChange={e => setDescription(e.target.value)}
            onBlur={e => onChange({ description: e.target.value })}
            size="small"
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
        {t('pages.marketing.communication.programmation')}
      </Typography>
      {isCashdeskChannel ? (
        <>
          <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ mb: 0.5, display: 'block' }}>
            Dates d&apos;édition
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, maxWidth: 360 }}>
            <TextField
              fullWidth
              label="Date de début"
              type="date"
              value={editionDateStart}
              onChange={e => setEditionDateStart(e.target.value)}
              onBlur={e => onChange({ editionDateStart: e.target.value })}
              size="small"
              disabled={data.isRecurring}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="Date de fin"
              type="date"
              value={editionDateEnd}
              onChange={e => setEditionDateEnd(e.target.value)}
              onBlur={e => onChange({ editionDateEnd: e.target.value })}
              size="small"
              disabled={data.isRecurring}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </>
      ) : (
        <TextField
          fullWidth
          label={t('pages.marketing.communication.sendDate')}
          type="datetime-local"
          value={sendDate}
          onChange={e => setSendDate(e.target.value)}
          onBlur={e => onChange({ sendDate: e.target.value })}
          size="small"
          disabled={data.isRecurring}
          InputLabelProps={{ shrink: true }}
          sx={{ maxWidth: 360 }}
        />
      )}

      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={data.isRecurring}
              onChange={e => onChange({ isRecurring: e.target.checked })}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: sidebarColor },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: sidebarColor },
              }}
            />
          }
          label={
            <Typography variant="body2">
              Ceci est un récurrent
            </Typography>
          }
        />
      </Box>

      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={data.sendByCustomer}
              onChange={e => onChange({ sendByCustomer: e.target.checked })}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: sidebarColor },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: sidebarColor },
              }}
            />
          }
          label={
            <Typography variant="body2">
              {t('pages.marketing.communication.sendByCustomer')}
            </Typography>
          }
        />
      </Box>

      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={!data.hasOffer}
              onChange={e => onChange({ hasOffer: !e.target.checked })}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: sidebarColor },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: sidebarColor },
              }}
            />
          }
          label={
            <Typography variant="body2">
              {t('pages.marketing.communication.noOffer')}
            </Typography>
          }
        />
      </Box>

      {!data.hasOffer && (
        <Box sx={{ display: 'flex', gap: 2, mt: 2, maxWidth: 360 }}>
          <TextField
            fullWidth
            label={t('pages.marketing.communication.followUpDateStart')}
            type="date"
            value={followUpDateStart}
            onChange={e => setFollowUpDateStart(e.target.value)}
            onBlur={e => onChange({ followUpDateStart: e.target.value })}
            size="small"
            disabled={data.isRecurring}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            label={t('pages.marketing.communication.followUpDateEnd')}
            type="date"
            value={followUpDateEnd}
            onChange={e => setFollowUpDateEnd(e.target.value)}
            onBlur={e => onChange({ followUpDateEnd: e.target.value })}
            size="small"
            disabled={data.isRecurring}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      )}

      {isTestOperationWorkspace && data.channel === 'cashdesk' && (
        <Box sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={data.isTestOperation}
                onChange={e => onChange({ isTestOperation: e.target.checked })}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: sidebarColor },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: sidebarColor },
                }}
              />
            }
            label={
              <Typography variant="body2">
                {t('pages.marketing.communication.testOperation')}
              </Typography>
            }
          />
        </Box>
      )}
    </Box>
  );
}

export default memo(CommunicationDefinitionStep);
